import { IPText } from 'baseComponents';
import { globalFontBoldWeight } from 'globalStyles';

const formatTrend = (n: number, showPercentSign: boolean) => {
  let abs = Math.abs(n);

  // cap the displayed value because 43586% has the same meaning as 999% for most users and it looks cleaner.
  if (abs > 999) abs = 999;

  const isInt = Number.isInteger(abs);
  const val = isInt ? abs.toFixed(0) : abs.toFixed(1);

  const percentSign = showPercentSign ? '%' : '';

  if (n > 0) return `+${val}${percentSign}`;
  if (n < 0) return `-${val}${percentSign}`;

  return `0${percentSign}`;
};

interface TrendLineProps {
  value: number; // 0 => equal, >0 => higher, <0 => lower
  reverse?: boolean; // when true, positive trend is red and negative trend is green
  asPercent?: boolean; // when true, show % sign after the number. Default true
  width?: number; // default 30
  strokeWidth?: number; // default 3
  right?: number; // default 6
}

export const TrendLine = ({ value, reverse, asPercent, width = 30, strokeWidth = 3, right = 6 }: TrendLineProps) => {
  const isHigher = value > 0;
  const isLower = value < 0;
  const isEqual = value === 0;

  const colors = {
    red: '#C62828',
    green: '#2E7D32',
    yellow: '#F9A825',
  };

  const trendColor = reverse
    ? isHigher
      ? colors.red
      : isLower
      ? colors.green
      : colors.yellow
    : isHigher
    ? colors.green
    : isLower
    ? colors.red
    : colors.yellow;

  return (
    <span
      style={{
        position: 'absolute',
        right,
        top: '50%',
        transform: 'translateY(-60%)', // slightly above center so % can sit under it
        width,
        height: 22,
        zIndex: 0, // behind the number
        pointerEvents: 'none',
      }}
    >
      <svg width={width} height={14} viewBox={`0 0 ${width} 14`} style={{ display: 'block' }} aria-hidden="true">
        {isHigher && (
          <line x1="0" y1="14" x2={width} y2="0" stroke={trendColor} strokeWidth={strokeWidth} strokeLinecap="round" />
        )}
        {isLower && (
          <line x1="0" y1="0" x2={width} y2="14" stroke={trendColor} strokeWidth={strokeWidth} strokeLinecap="round" />
        )}
        {isEqual && (
          <line x1="0" y1="7" x2={width} y2="7" stroke={trendColor} strokeWidth={strokeWidth} strokeLinecap="round" />
        )}
      </svg>

      <IPText
        styles={{
          root: {
            position: 'absolute',
            top: 14, // beneath the line
            fontSize: 12,
            fontWeight: globalFontBoldWeight,
            whiteSpace: 'nowrap',
            ...(isHigher ? { right: 0 } : isLower ? { left: 0 } : { left: '50%', transform: 'translateX(-50%)' }),
          },
        }}
      >
        {formatTrend(value, asPercent ?? true)}
      </IPText>
    </span>
  );
};
